import { Button, useToast } from '@kcd/design-system';
import { trackEvent } from '@kcd/event-tracker';

import { CHECKOUT_ROUTER, VENDORS_ROUTER } from '~/constants/router';
import { useUpdateECoponQuick } from '~/domains/cart/queries';
import ProductPrice from '~/domains/products/components/ProductPrice';
import ProductImage from '~/domains/products/ProductImage';
import { useTrackProductEventProps } from '~/hooks/event';
import { useLinkComponent } from '~/hooks/mobileApp';
import { usePushWithQuery } from '~/hooks/url';
import { getOAIErrorMessage } from '~/libs/openapi';
import { OAIApiError } from '~/libs/openApiError';
import type { UpdateECouponQuickReqParams } from '~/pages/vendors/[vendorId]/products/[offerId]/components/ECoupon/ECouponOrderButton';
import type { OAISchema } from '~/types/openapi';

type Props = {
  data: OAISchema['MarketSelectProductResponseV2'];
};

const FrequentlyOrderedProductCard = ({ data }: Props) => {
  const LinkComponent = useLinkComponent();
  const updateQuickECoupon = useUpdateECoponQuick();
  const nextPush = usePushWithQuery();
  const toast = useToast();
  const {
    offerId,
    vendorId,
    mainImages,
    productName,
    isOutOfStock,
    status,
    orderCount,
  } = data;
  const [{ onClick }] = useTrackProductEventProps(data);

  const onClickProduct = () => {
    onClick?.();
  };

  const onClickECouponOrder = async () => {
    trackEvent('clicked purchaseButton in 마켓', {
      placement: '마이페이지',
      productName,
      offerId,
      path: window.location.pathname,
    });

    const requestBody: UpdateECouponQuickReqParams = {
      offerId: offerId!,
    };
    try {
      await updateQuickECoupon({
        requestBody,
      });
      void nextPush(CHECKOUT_ROUTER.eCouponRoot, false);
    } catch (error) {
      if (error instanceof OAIApiError) {
        const errMessage = getOAIErrorMessage(error);
        toast(errMessage, {
          offset: 86,
        });
      }
      throw error;
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1">
        <LinkComponent href={VENDORS_ROUTER.productDetail(vendorId, offerId!)}>
          <div role="presentation" onClick={onClickProduct}>
            <ProductImage
              width={140}
              height={140}
              sizes="45vw"
              layout="responsive"
              src={mainImages![0]}
              alt={productName}
              isOutOfStock={isOutOfStock}
              status={status}
              className="!mb-6"
            />
            {orderCount && (
              <div className="text-primary-regular typo-footnote300-semibold">
                {orderCount}회 이상 구매한 상품
              </div>
            )}
            <div
              className="mb-4 mt-6 line-clamp-1 whitespace-pre-line text-normal"
              style={{ fontSize: '14px' }}
            >
              {productName}
            </div>

            <div className="mb-6">
              <ProductPrice {...data} reverse />
            </div>
          </div>
        </LinkComponent>
      </div>
      <Button
        variant="outline"
        size="sm"
        block
        className="text-normal typo-paragraph300-semibold"
        onClick={onClickECouponOrder}
      >
        재구매 하기
      </Button>
    </div>
  );
};

export default FrequentlyOrderedProductCard;
